import type { MutableRefObject } from 'react';
import { useInsertionEffect, useRef } from 'react';

const isObject = (obj: unknown): boolean =>
    obj != null &&
    typeof obj === 'object' &&
    (Object.getPrototypeOf(obj) === Object.prototype || Array.isArray(obj));

const canShallowCopy = (a: unknown, b: unknown): a is {} =>
    isObject(a) && isObject(b) && Object.getPrototypeOf(a) === Object.getPrototypeOf(b);

type ActualRefOptions = {
    /**
     * как копировать существующее значение
     *  - `'ref'` - просто заменять объект, вариант по умолчанию
     *  - `'shallow'` - поверхностное копирование для простых объектов и массивов
     */
    copyVariant: 'ref' | 'shallow';
};

/**
 * Реф с обновляемым значением
 * @param value значение
 * @param options дополнительные опции
 * @returns реф
 */
export function useActualRef<V>(value: V, options?: ActualRefOptions): MutableRefObject<V> {
    const ref = useRef(value);

    useInsertionEffect(() => {
        if (options?.copyVariant === 'shallow' && canShallowCopy(ref.current, value)) {
            Object.assign(ref.current, value);
        } else {
            ref.current = value;
        }
    });

    return ref;
}
